import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { http, errorText } from "@/lib/api";
import { fileUrl } from "@/lib/images";
import { AdminTitle } from "./AdminLayout";

const SECTIONS = [
  ["about", "Om Liisa", "Første bilde er hovedbildet, det andre vises som et lite bilde ved siden av."],
  ["gallery", "Galleri", "Bildene vises i galleriet på nettsiden i denne rekkefølgen."],
];

const ImageCard = ({ img, onChange }) => {
  const [caption, setCaption] = useState(img.caption);
  const saveCaption = async () => {
    if (caption === img.caption) return;
    await http.patch(`/admin/images/${img.id}`, { caption });
    toast.success("Bildetekst lagret");
    onChange();
  };
  const remove = async () => {
    if (!window.confirm("Fjerne bildet fra nettsiden?")) return;
    await http.delete(`/admin/images/${img.id}`);
    onChange();
  };
  return (
    <div className="border border-[#E5E0D8]" data-testid={`admin-image-${img.id}`}>
      <img src={fileUrl(img.storage_path)} alt={img.caption} className="w-full h-48 object-cover object-top" />
      <div className="p-3 flex gap-2">
        <Input value={caption} onChange={(e) => setCaption(e.target.value)} onBlur={saveCaption} placeholder="Bildetekst" className="rounded-none h-9 text-sm" data-testid={`image-caption-${img.id}`} />
        <button onClick={remove} className="p-2 text-[#5C5A56] hover:text-red-700" data-testid={`image-delete-${img.id}`}><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
      </div>
    </div>
  );
};

const UploadButton = ({ category, onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const files = [...e.target.files];
    if (!files.length) return;
    setBusy(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", category);
      fd.append("caption", "");
      try { await http.post("/admin/images", fd); } catch (err) { toast.error(errorText(err)); }
    }
    setBusy(false);
    e.target.value = "";
    toast.success("Opplastet");
    onDone();
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={upload} data-testid={`image-upload-input-${category}`} />
      <button onClick={() => ref.current.click()} disabled={busy} className="flex items-center gap-2 px-5 py-2.5 bg-[#1A1A1A] text-white text-sm hover:bg-[#8C6E48] transition-colors disabled:opacity-50" data-testid={`image-upload-btn-${category}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.5} />} Last opp bilder
      </button>
    </>
  );
};

export default function ImagesAdmin() {
  const [imgs, setImgs] = useState([]);
  const load = () => http.get("/admin/images").then((r) => setImgs(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="admin-images">
      <AdminTitle title="Bilder" sub="Last opp og bytt bilder til «Om Liisa» og galleriet. JPG, PNG eller WEBP, maks 10 MB per bilde." />
      {SECTIONS.map(([cat, title, hint]) => (
        <section key={cat} className="mb-14" data-testid={`image-section-${cat}`}>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-5">
            <div>
              <h2 className="text-2xl font-medium">{title}</h2>
              <p className="text-sm text-[#5C5A56] mt-1">{hint}</p>
            </div>
            <UploadButton category={cat} onDone={load} />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {imgs.filter((i) => i.category === cat).map((i) => <ImageCard key={i.id} img={i} onChange={load} />)}
          </div>
        </section>
      ))}
    </div>
  );
}
